import type { CSSProperties } from 'react';
import { useNodeId } from '../../components';
import type { ResizeControlPosition } from '../../core';
import type { ResizeCallbacks } from '../../interactions';
import { ResizeControl } from './ResizeControl';

const LINE_CONTROLS: ResizeControlPosition[] = ['top', 'right', 'bottom', 'left'];
const HANDLE_CONTROLS: ResizeControlPosition[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
/** v11 defaults. */
const DEFAULT_MIN_SIZE = 10;

export type NodeResizerProps = ResizeCallbacks & {
  nodeId?: string;
  isVisible?: boolean;
  color?: string;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  handleClassName?: string;
  handleStyle?: CSSProperties;
  lineClassName?: string;
  lineStyle?: CSSProperties;
};

/**
 * v11 `<NodeResizer>`: four side lines and four corner handles around the node it is rendered in. Resizing reports
 * `dimensions` changes with `updateStyle` (the node gets `style.width/height`) and calls `onResize` / `onResizeEnd`.
 */
export const NodeResizer = ({
  nodeId,
  isVisible = true,
  color,
  minWidth = DEFAULT_MIN_SIZE,
  minHeight = DEFAULT_MIN_SIZE,
  maxWidth = Number.MAX_VALUE,
  maxHeight = Number.MAX_VALUE,
  handleClassName,
  handleStyle,
  lineClassName,
  lineStyle,
  onResizeStart,
  onResize,
  onResizeEnd,
}: NodeResizerProps) => {
  const contextNodeId = useNodeId();
  if (!isVisible) return null;
  const id = nodeId ?? contextNodeId;
  const shared = { nodeId: id, color, limits: { minWidth, minHeight, maxWidth, maxHeight }, onResizeStart, onResize, onResizeEnd };
  return (
    <>
      {LINE_CONTROLS.map((position) => (
        <ResizeControl key={position} position={position} variant="line" className={lineClassName} style={lineStyle} {...shared} />
      ))}
      {HANDLE_CONTROLS.map((position) => (
        <ResizeControl key={position} position={position} variant="handle" className={handleClassName} style={handleStyle} {...shared} />
      ))}
    </>
  );
};
